import { isSurveySection } from '../types';
import type { SurveyPart, SurveyQuestion, SurveySection } from '../types';

/**
 * Reorder a section's questions to match `orderedIds` (all question ids in their new order).
 * Any question NOT in orderedIds is kept, in its original relative order, at the end — a
 * defensive fallback; callers now pass every question id, so nothing is normally appended.
 */
export function reorderQuestions(live: SurveyQuestion[], orderedIds: string[]): SurveyQuestion[] {
	const byId = new Map(live.map(q => [q.id, q]));
	const reordered = orderedIds.map(id => byId.get(id)).filter((q): q is SurveyQuestion => !!q);
	const remaining = live.filter(q => !orderedIds.includes(q.id));
	return [...reordered, ...remaining];
}

/**
 * Rebuild `surveyParts` with sections reordered to `orderedSectionIds`, while preserving
 * any non-section (flat) parts at their ORIGINAL indices. Any section not in
 * orderedSectionIds keeps its original relative order after the ordered ones — a defensive
 * fallback; callers now pass every section id, so nothing is normally appended.
 */
export function reorderParts(liveParts: SurveyPart[], orderedSectionIds: string[]): SurveyPart[] {
	const sectionById = new Map(liveParts.filter(isSurveySection).map(s => [s.id, s]));
	const ordered = orderedSectionIds.map(id => sectionById.get(id)).filter((s): s is SurveySection => !!s);
	const remaining = liveParts.filter(
		(p): p is SurveySection => isSurveySection(p) && !orderedSectionIds.includes(p.id)
	);
	const queue: SurveySection[] = [...ordered, ...remaining];
	let si = 0;
	return liveParts.map(p => (isSurveySection(p) ? queue[si++] : p)) as SurveyPart[];
}
